import { request } from './api-client';
import type { SchemaDescriptor } from './api-types';
export type Segment =
  | { kind: 'literal'; text: string }
  | {
      kind: 'field';
      source: 'context' | 'recipient';
      fieldId: string;
      fieldType: string;
      format: 'text' | 'date' | 'currency';
      currency?: string;
      fallback?: string;
    };
export type ContextSelector =
  | { kind: 'context' | 'recipient' | 'correspondence' }
  | { kind: 'note'; recordId: string };
export type MessageSpec =
  | { mode: 'exact'; subject: Segment[]; body: Segment[] }
  | {
      mode: 'guidance';
      purpose: string;
      instructions: string;
      example?: string;
      requiredContext: ContextSelector[];
      optionalContext: ContextSelector[];
    };
export type Recipient = {
  relationFieldId?: string;
  emailFieldId: string;
  address?: string;
};
export type TemplateDef = {
  contextObjectId: string;
  recipient: Recipient;
  message: MessageSpec;
};
export type Condition = {
  fieldId: string;
  operator: 'equals' | 'not_equals' | 'is_set' | 'is_not_set';
  value?: string | number | boolean | null;
};
export type Delay = {
  amount: number;
  unit: 'hours' | 'days' | 'business_days';
};
export type Step = {
  id: string;
  threadMode: 'new' | 'reply_previous';
  message: { templateVersionId: string } | { inline: MessageSpec };
  afterPreviousSend?: Delay;
};
export type AutomationDef = {
  contextObjectId: string;
  recipient: Recipient;
  connectionId: string;
  timezone: string;
  trigger:
    | { kind: 'manual' | 'created' }
    | {
        kind: 'field_changed';
        fieldId: string;
        to: string | number | boolean | null;
        from?: string | number | boolean | null;
      };
  conditions: Condition[];
  stopConditions: Condition[];
  steps: Step[];
  limits: { active: number; daily: number };
};
export type Kind = 'templates' | 'automations';
export interface EmailResource<D = TemplateDef | AutomationDef> {
  id: string;
  name: string;
  definition: D;
  revision: number;
  published_version_id: string | null;
  state?: string;
  updated_at: string;
  versions?: Array<{
    id: string;
    version: number;
    definition: D;
    template_id?: string;
  }>;
  dependencies?: Array<{
    id: string;
    name: string;
    definition: AutomationDef;
    published_definition?: AutomationDef;
  }>;
  existingRuns?: number;
  usedBy?: number;
  stepMessages?: Array<{ stepId: string; message?: MessageSpec; templateVersionId?: string; error?: string }>;
  counts?: Array<{ state: string; count: number }>;
}
export interface Enrollment {
  id: string;
  automation_id: string;
  context_record_id: string;
  context_name?:string;
  address: string | null;
  state: string;
  reason: string | null;
  explanation: string;
  revision: number;
  step_index: number;
  due_at: string | null;
  name?: string;
  definition: AutomationDef;
  steps: Array<{
    id: string;
    step_id: string;
    state: string;
    thread_id: string | null;
    draft_id: string | null;
    send_attempt_id: string | null;
    accepted_at: string | null;
    generation?: {
      status: string;
      lastError: string | null;
      result: { outcome: string; reason: string; questions?: string[] } | null;
      costMicrounits: number | null;
    } | null;
  }>;
  events: Array<{
    id: string;
    kind: string;
    created_at: string;
    details: Record<string, unknown>;
  }>;
}
export interface Preview {
  id: string;
  resource_revision: number;
  input_hash: string;
  snapshot: {
    resolved: {
      address: string;
      context: { id: string; name: string };
      recipient: { id: string; name: string };
    };
  };
  result: {
    subject?: string;
    bodyText?: string;
    outcome?: string;
    reason?: string;
    missing?: Array<{ fieldId: string; message: string }>;
    needsGeneration?: boolean;
    inference?: boolean;
    trigger?: { explanation: string };
    schedule?: unknown;
  };
  cost_microunits?: number;
}
export interface EnrollmentPreview {
  id: string;
  expires_at: string;
  selection: Array<{
    recordId: string;
    recipientId?: string;
    address?: string;
    excluded?: string | null;
    blocked?: string;
  }>;
}
const post = <T>(path: string, body: unknown, personalKey = false) =>
  request<T>(`/v1/email/${path}`, { method: 'POST', body, personalKey });
export const resourceApi = {
  capabilities: () => request<{ enabled: boolean }>('/v1/email/capabilities'),
  options: () =>
    request<{
      enabled: boolean;
      objects: SchemaDescriptor['objects'];
      policy: string;
      limits: { selection: number; active: number; daily: number };
    }>('/v1/email/options'),
  list: (
    kind: Kind,
    cursor?: string,
    filter: { query?: string; mode?: string } = {},
  ) =>
    request<{ items: EmailResource[]; nextCursor: string | null }>(
      `/v1/email/${kind}?${new URLSearchParams({ ...(cursor ? { cursor } : {}), ...(filter.query ? { q: filter.query } : {}), ...(filter.mode && filter.mode !== 'all' ? { mode: filter.mode } : {}) })}`,
    ),
  detail: (kind: Kind, id: string) =>
    request<EmailResource>(`/v1/email/${kind}/${id}`),
  save: (
    kind: Kind,
    body: {
      name: string;
      definition: TemplateDef | AutomationDef;
      expectedRevision?: number;
    },
    id?: string,
  ) =>
    request<EmailResource>(`/v1/email/${kind}${id ? `/${id}` : ''}`, {
      method: id ? 'PATCH' : 'POST',
      body,
    }),
  publish: (
    kind: Kind,
    id: string,
    expectedRevision: number,
    activate = false,
  ) => post(`${kind}/${id}/publish`, { expectedRevision, activate }),
  remove: (kind: Kind, id: string, expectedRevision: number) =>
    request(`/v1/email/${kind}/${id}`, {
      method: 'DELETE',
      body: { expectedRevision },
    }),
  preview: (
    kind: Kind,
    id: string,
    body: {
      recordId: string;
      connectionId?: string;
      expectedRevision: number;
      generate: boolean;
      regenerate?: boolean;
      published?: boolean;
    },
  ) => post<Preview>(`${kind}/${id}/preview`, body, body.generate),
  apply: (
    id: string,
    body: {
      previewId: string;
      idempotencyKey: string;
      draftId?: string;
      expectedRevision?: number;
      replaceEdited?: boolean;
    },
  ) => post<{ threadId: string }>(`templates/${id}/apply`, body),
  automationCommand: (
    id: string,
    command: 'pause' | 'resume',
    expectedRevision: number,
  ) => post(`automations/${id}/${command}`, { expectedRevision }),
  enrollmentPreview: (id: string, recordIds: string[]) =>
    post<EnrollmentPreview>(`automations/${id}/enrollment-preview`, {
      recordIds,
    }),
  enroll: (id: string, previewId: string, idempotencyKey: string) =>
    post<{ enrollmentIds: string[] }>(`automations/${id}/enroll`, {
      previewId,
      idempotencyKey,
    }),
  runs: (id: string, cursor?: string) =>
    request<{ items: Enrollment[]; nextCursor: string | null }>(
      `/v1/email/automations/${id}/enrollments${cursor ? `?cursor=${cursor}` : ''}`,
    ),
  run: (id: string) => request<Enrollment>(`/v1/email/enrollments/${id}`),
  confirmTime: (id: string, acceptedAt: string) =>
    post(`send-attempts/${id}/confirm-time`, {
      confirmedAfterCheckingGmail: true,
      acceptedAt,
    }),
  runCommand: (
    id: string,
    command: string,
    expectedRevision: number,
    idempotencyKey = crypto.randomUUID(),
  ) =>
    post(`enrollments/${id}/${command}`, { expectedRevision, idempotencyKey }),
  recordRuns: (id: string) =>
    request<{ items: Enrollment[] }>(`/v1/records/${id}/email-enrollments`),
};
export const emptyMessage = (): MessageSpec => ({
  mode: 'exact',
  subject: [{ kind: 'literal', text: '' }],
  body: [{ kind: 'literal', text: '' }],
});
